import React, { useState } from 'react';
import MagnifyingGlass from '../Icons/MagnifyingGlass';
import { isNullOrUndefined } from '../../helpers/object-helpers';

import styles from './TextInput.module.scss';
import AssistiveText from '../AssistiveText/AssistiveText';
import blurHandler from '../../helpers/blur-handler';
import ValidationMessage from '../ValidationMessage/ValidationMessage';

export interface ITextInput {
  id: string;
  label: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (keyboardEvent: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (keyboardEvent: React.KeyboardEvent<HTMLInputElement>) => void;
  errorMessage?: string;
  assistiveText?: string;
  disabled?: boolean;
  isValid?: boolean;
  type?: 'email' | 'password' | 'text' | 'search' | 'tel';
  value?: string;
  size?: 'small' | 'large';
  validText?: string;
}

export default function TextInput({
  id,
  label,
  onChange,
  onKeyDown,
  onKeyUp,
  placeholder,
  errorMessage,
  assistiveText,
  disabled,
  isValid,
  type = 'text',
  value,
  size = 'large',
  validText,
}: ITextInput) {
  const [touched, setTouched] = useState<boolean>(false);
  const [valueCache, setValueCache] = useState<string | undefined>(
    value === '' ? undefined : value,
  );

  if (value !== valueCache && touched) {
    setValueCache(value);
  }

  const handleBlur = (): void => {
    if (isNullOrUndefined(valueCache)) return;
    setTouched(true);
    if (!isNullOrUndefined(onChange)) onChange(valueCache);
  };

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValueCache(event.target.value);
    if (touched && !isNullOrUndefined(onChange)) onChange(event.target.value);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!isNullOrUndefined(onKeyDown)) onKeyDown(event);
  }

  function handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!isNullOrUndefined(onKeyUp)) onKeyUp(event);
  }

  const inputValidationClass = !isNullOrUndefined(isValid)
    ? isValid
      ? styles.validInput
      : styles.invalidInput
    : '';

  const inputSearchClass = type === 'search' ? styles.searchInput : '';

  const inputSizeClass = size === 'small' ? styles.smallInput : styles.largeInput;

  return (
    <>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.inputContainer}>
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          aria-invalid={isValid !== null ? isValid === false : undefined}
          disabled={disabled}
          autoComplete="off"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onBlur={(e) => {
            blurHandler(e, handleBlur);
          }}
          value={valueCache ?? ''}
          className={[inputValidationClass, inputSearchClass, inputSizeClass].join(' ')}
        />
        {type === 'search' && <SearchIcon />}
      </div>
      <ValidationMessage isValid={isValid} errors={[errorMessage]} validText={validText} />
      <AssistiveText>{assistiveText}</AssistiveText>
    </>
  );
}

const SearchIcon = () => (
  <div className={styles.searchIconContainer}>
    <MagnifyingGlass />
  </div>
);
